import { create, tsx } from '@dojo/framework/core/vdom';
import { Head } from '../widgets/Head';
import { Hero } from '../widgets/Hero';
import * as css from './Contact.m.css';
import icache from '@dojo/framework/core/middleware/icache';
import ContentWrapper from '../widgets/ContentWrapper';
import Section from '../widgets/Section';

const factory = create({ icache });

export const Contact = factory(function Contact({ middleware: { icache } }) {
	const submitError = icache.getOrSet('submitError', false);

	return (
		<div classes={css.root}>
			<Head
				title="Contact Us | SitePen"
				description="With offices throughout the US and UK, we’re available to to chat at your convenience. Send us a note to hello@sitepen.com."
			/>
			<Hero background="level1RedOrange" align="center" paddingBottom="sm">
				{{
					xlTitle: `Let's Connect`
				}}
			</Hero>
			<Section type="leading">
				<ContentWrapper>
					<div classes={css.content}>
						<form
							classes={css.leading}
							onsubmit={(event: Event) => {
								event.stopPropagation();
								event.preventDefault();
								icache.set('submitError', false);

								const url =
									'https://script.google.com/macros/s/AKfycbxGw_dH7fz1iDh1A619ssMuXEkZX--5MsKTpIKUS_KSO9n-LyiX18zEhVDkCl-IXzoF/exec';
								const form = event.target as HTMLFormElement;

								const gotcha =
									(form.elements.namedItem('_gotcha') as HTMLInputElement) ||
									(form as any)['_gotcha'];
								if (gotcha && gotcha.value) {
									// Bot: silently drop
									return false;
								}

								const { elements } = form;
								const fields: { name: string; value: string }[] = [];

								for (let i = 0; i < elements.length; i++) {
									const field = elements[i] as
										| HTMLInputElement
										| HTMLTextAreaElement;
									if (!field.name || field.name === '_gotcha') continue;

									let value = field.value;
									if (
										field instanceof HTMLInputElement &&
										field.type === 'checkbox'
									) {
										value = field.checked ? 'on' : '';
									}

									fields.push({ name: field.name, value });
								}

								const payload = JSON.stringify({
									submittedAt: Date.now(),
									context: {
										pageUri: 'https://www.sitepen.com/contact',
										pageName: 'Sitepen Website Contact Form'
									},
									fields
								});

								// Prefer sendBeacon for reliability on navigation
								if (navigator.sendBeacon) {
									const blob = new Blob([payload], {
										type: 'text/plain;charset=UTF-8'
									});
									navigator.sendBeacon(url, blob);
								} else {
									// Fallback: fire-and-forget fetch with no-cors
									fetch(url, {
										method: 'POST',
										mode: 'no-cors',
										headers: {
											'Content-Type': 'text/plain;charset=UTF-8'
										},
										body: payload
									}).catch(() => {
										icache.set('submitError', true);
									});
								}
								window.location.href = '/contact-thank-you';
								return false;
							}}
						>
							<div classes={css.row}>
								<div classes={css.field}>
									<label classes={css.label} for="firstname">
										First Name
										<span classes={css.required} aria-hidden="true">
											*
										</span>
									</label>
									<input
										classes={css.input}
										id="firstname"
										name="firstname"
										type="text"
										required
										autocomplete="given-name"
									/>
								</div>
								<div classes={css.field}>
									<label classes={css.label} for="lastname">
										Last Name
										<span classes={css.required} aria-hidden="true">
											*
										</span>
									</label>
									<input
										classes={css.input}
										id="lastname"
										name="lastname"
										type="text"
										required
										autocomplete="family-name"
									/>
								</div>
							</div>
							<div classes={css.row}>
								<label tabindex="-1" aria-hidden="true" classes={css.gotcha}>
									gotcha label
									<input
										tabindex="-1"
										aria-hidden="true"
										id="_gotcha"
										name="_gotcha"
										type="text"
										autocomplete="off"
									/>
								</label>
								<div classes={css.field}>
									<label classes={css.label} for="company">
										Company Name
										<span classes={css.required} aria-hidden="true">
											*
										</span>
									</label>
									<input
										classes={css.input}
										id="company"
										name="company"
										type="text"
										required
										autocomplete="organization"
									/>
								</div>
								<div classes={css.field}>
									<label classes={css.label} for="phone">
										Phone Number
										<span classes={css.required} aria-hidden="true">
											*
										</span>
									</label>
									<input
										classes={css.input}
										id="phone"
										name="phone"
										type="tel"
										required
										autocomplete="tel"
										minLength={7}
									/>
								</div>
							</div>
							<div classes={css.row}>
								<div classes={css.field}>
									<label classes={css.label} for="email">
										Email Address
										<span classes={css.required} aria-hidden="true">
											*
										</span>
									</label>
									<input
										classes={css.input}
										id="email"
										name="email"
										type="email"
										required
										autocomplete="email"
									/>
								</div>
							</div>
							<div classes={css.row}>
								<div classes={css.field}>
									<label classes={css.label} for="how_can_we_help_">
										How can we help?
									</label>
									<textarea
										classes={css.textarea}
										id="how_can_we_help_"
										name="how_can_we_help_"
									/>
								</div>
							</div>
							<div classes={css.rowNoMargin}>
								<div classes={css.field}>
									<label for="newsletter_opt_in">
										<input
											type="checkbox"
											name="newsletter_opt_in"
											id="newsletter_opt_in"
										/>{' '}
										Let me know when SitePen shares info on Enterprise
										JavaScript!
									</label>
								</div>
							</div>
							<button type="submit" classes={css.submit}>
								Submit
							</button>
							{submitError && (
								<p classes={css.error}>
									Something went wrong with your form submission. Please try again
								</p>
							)}
						</form>
						<div classes={css.trailing}>
							<h2 classes={css.addressHeading}>Address</h2>
							<address itemscope itemtype="http://schema.org/PostalAddress">
								<span itemprop="streetAddress">
									228 Hamilton Avenue 
									<br /> Third Floor
								</span>
								<br />
								<span itemprop="addressLocality">Palo Alto</span>
								{', '}
								<span itemprop="addressRegion">CA</span>{' '}
								<span itemprop="postalCode">94301</span>
								<br />
							</address>
						</div>
					</div>
				</ContentWrapper>
			</Section>
		</div>
	);
});

export default Contact;
